import { useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { t } from "@/i18n-js/instance";
import { TextSelect } from "@/react/components/shared/TextSelect";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import { HEADING_DEFAULT_DATA, TEXT_SELECT_OPTIONS } from "./constants";
import type { HeadingType } from "./constants";
import { isHeadingType } from "./typePredicates";

interface HeadingLevelPickerProps {
  label: string;
}

export const HeadingLevelPicker = ({ label }: HeadingLevelPickerProps) => {
  const { data, updateSelectedBlock } = useBuilderStore(state => ({
    data: state.getSelectedBlock(),
    updateSelectedBlock: state.updateSelectedBlock,
  }));
  const selectedBlock = data?.block;

  const isHeadingBlock = useMemo(
    () => !!selectedBlock && isHeadingType(selectedBlock.type),
    [selectedBlock],
  );

  const handleHeadingChange = useCallback(
    (newType: HeadingType) => {
      if (!selectedBlock) return;

      const defaultData = HEADING_DEFAULT_DATA[newType];

      updateSelectedBlock({
        ...selectedBlock,
        id: uuidv4(),
        type: newType,
        meta: defaultData.meta,
        props: {
          ...selectedBlock.props,
          text: selectedBlock.props.text,
          styles: defaultData.props.styles,
        },
      });
    },
    [selectedBlock, updateSelectedBlock],
  );

  if (!isHeadingBlock || !selectedBlock) return null;

  const headingType = selectedBlock.type as HeadingType;

  return (
    <div>
      <label className="form-group__label text-dark text-xs font-normal">
        {label}
      </label>
      <TextSelect
        options={TEXT_SELECT_OPTIONS}
        value={headingType}
        onChange={handleHeadingChange}
        placeholder={t("builder_ui.prop_type.heading_level")}
        buttonClassName="w-full"
      />
    </div>
  );
};
